import React, { useState, useRef } from "react";
import { useNavigate } from "react-router-dom";
import { useTranslation } from 'react-i18next';
import {
  Card,
  CardContent,
  Typography,
  Divider,
  Grid,
  useTheme,
  Alert,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  List,
  ListItem,
  ListItemText,
  Button,
  Box,
} from "@mui/material";
import Icons from "../../Utils/Icons";
import * as XLSX from "xlsx";
import { POST } from "../Generic/CrudRequest";
import EncryptionUtils from "../../Utils/EncryptionUtils";
import fileToDownloadES from "../../Templates/ImportacionDeUnidades.xlsx";
import fileToDownloadEN from "../../Templates/ImportUnitsTemplate.xlsx";
import Alerts from "../../Utils/Alerts";

const requiredColumns = [
  "VIN",
  "Modelo",
  "Marca",
  "Año",
  "Ubicación de Origen",
  "Ubicación de Destino",
  "Cliente",
];

export default function ImportUnits({ config }) {
  const { t } = useTranslation();
  const theme = useTheme();
  const navigate = useNavigate();
  const [excelData, setExcelData] = useState([]);
  const [error, setError] = useState("");
  const [showAlert, setShowAlert] = useState(false);
  const [alertType, setAlertType] = useState("");
  const [alertMessage, setAlertMessage] = useState("");
  const [fileName, setFileName] = useState("");
  const [fileSize, setFileSize] = useState(0);
  const fileInputRef = useRef(null);

  const handleFileUpload = (e) => {
    const file = e.target.files[0];
    handleFile(file);
  };

  const handleFileDrop = (e) => {
    e.preventDefault();
    const file = e.dataTransfer.files[0];
    handleFile(file);
  };

  const handleFile = (file) => {
    const reader = new FileReader();

    reader.onload = async (event) => {
      const binaryString = event.target.result;
      setFileName(file.name);
      setFileSize(file.size);
      const workbook = XLSX.read(binaryString, { type: "binary" });
      const sheetName = workbook.SheetNames[0];
      const sheet = workbook.Sheets[sheetName];
      const data = XLSX.utils.sheet_to_json(sheet, { header: 1 });

      const firstRow = data[0];
      const missingColumns = requiredColumns.filter(
        (column) => !firstRow.includes(column)
      );

      if (missingColumns.length > 0) {
        setError(
          `${t("units.importUnits.requiredColumns")} ${missingColumns.join(
            ", "
          )}`
        );
        setExcelData([]);
      } else {
        setError("");

        const dataUser = EncryptionUtils.getDecryptedData("user");
        const parsedUser = JSON.parse(dataUser);

        const importLoadModels = data.slice(1).map((rowData) => {
          const modelData = {
            vin: rowData[0] || "",
            modelo: rowData[1] || "",
            idModelo: 0,
            marca: rowData[2] || "",
            idMarca: 0,
            anio: rowData[3] || "",
            cliente: rowData[4] || "",
            idCliente: 0,
            ubicacion: rowData[5] || "",
            idUbicacion: 0,
            ubicacionDestino: rowData[6] || "",
            idUbicacionDestino: 0,
            estatus: true,
            idUsuario: parsedUser.userId,
            mensaje: [],
          };

          if (modelData.vin === "" || modelData.vin === undefined) {
            modelData.mensaje.push(t("units.importUnits.vinRequired"));
          }

          if (modelData.anio === "" || modelData.anio === undefined) {
            modelData.mensaje.push(t("units.importUnits.yearRequired"));
          }

          if (modelData.ubicacion === "" || modelData.ubicacion === undefined) {
            modelData.mensaje.push(t("units.importUnits.originLocationRequired"));
          }

          if (
            modelData.ubicacionDestino === "" ||
            modelData.ubicacionDestino === undefined
          ) {
            modelData.mensaje.push(t("units.importUnits.destinationLocationRequired"));
          }

          if (modelData.cliente === "" || modelData.cliente === undefined) {
            modelData.mensaje.push(t("units.importUnits.clientRequired"));
          }

          if (modelData.marca === "" || modelData.marca === undefined) {
            modelData.mensaje.push(t("units.importUnits.brandRequired"));
          }

          if (modelData.modelo === "" || modelData.modelo === undefined) {
            modelData.mensaje.push(t("units.importUnits.modelRequired"));
          }

          if (modelData.mensaje.length > 0) {
            modelData.estatus = false;
          }

          return modelData;
        });

        const vinCounts = importLoadModels.reduce((acc, model) => {
          acc[model.vin] = (acc[model.vin] || 0) + 1;
          return acc;
        }, {});

        importLoadModels.forEach((model) => {
          if (vinCounts[model.vin] > 1) {
            model.mensaje.push(t("units.importUnits.duplicateVin"));
            model.estatus = false;
          }
        });

        const validModels = importLoadModels.filter((model) => model.estatus);
        const responseUnits = await unitsError(validModels);

        responseUnits.forEach((responseModel) => {
          const matchingModel = importLoadModels.find(
            (model) => model.vin === responseModel.vin
          );
          if (matchingModel) {
            matchingModel.mensaje.push(...responseModel.mensaje);
            matchingModel.estatus = responseModel.estatus;
            matchingModel.idUbicacion = responseModel.idUbicacion;
            matchingModel.idUbicacionDestino = responseModel.idUbicacionDestino;
            matchingModel.idCliente = responseModel.idCliente;
            matchingModel.idMarca = responseModel.idMarca;
            matchingModel.idModelo = responseModel.idModelo;
          }
        });

        importLoadModels.forEach((importLoadModel) => {
          if (importLoadModel.estatus === true) {
            importLoadModel.mensaje.push(t("units.importUnits.canImport"));
          }
        });

        setExcelData(importLoadModels);
      }
    };

    reader.readAsBinaryString(file);
  };

  const unitsError = async (units) => {
    const unitsError = await POST(
      `${config.url}/api/Unit/Validar-excel`,
      units
    );
    return unitsError;
  };

  const saveImportLoad = async () => {
    if (excelData.length > 0) {
      const dataSend = excelData.filter((model) => model.estatus);

      if (dataSend.length > 0) {
        await POST(`${config.url}/api/Unit/Insertar-Excel`, dataSend);
        navigate("/Unidades");
      } else {
        const message = t("units.importUnits.noValidUnits");
        const typeMessage = "warning";
        setAlertMessage(message);
        setAlertType(typeMessage);
        setShowAlert(true);
      }
    } else {
      const message = t("units.importUnits.noUnitsToImport");
      const typeMessage = "warning";
      setAlertMessage(message);
      setAlertType(typeMessage);
      setShowAlert(true);
    }
  };

  const handleClearData = () => {
    setExcelData([]);
    setFileName("");
    setFileSize(0);
    fileInputRef.current.value = null;
  };

  const handleSubmit = () => {
    saveImportLoad();
  };

  const handleDownload = () => {
    const link = document.createElement("a");
    // Seleccionar el archivo según el idioma actual
    const currentLanguage = localStorage.getItem("language") || "es";
    const fileToDownload = currentLanguage === "en" ? fileToDownloadEN : fileToDownloadES;
    const fileName = currentLanguage === "en" ? "ImportUnitsTemplate.xlsx" : "ImportacionDeUnidades.xlsx";
    
    link.href = fileToDownload;
    link.download = fileName;
    link.click();

    URL.revokeObjectURL(link.href);
  };

  const handleCloseAlert = () => {
    setShowAlert(false);
  };

  const handleDragOver = (e) => {
    e.preventDefault();
  };

  return (
    <>
      {showAlert && (
        <Alerts
          error={alertMessage}
          type={alertType}
          onClose={handleCloseAlert}
        />
      )}
      <Card
        variant="outlined"
        sx={{
          overflow: "hidden",
          boxShadow: "0 0 10px rgba(0, 0, 0, 0.2)",
          border: "1px solid rgba(0, 0, 0, 0.1)",
          borderRadius: 2,
        }}
      >
        <CardContent>
          <CardContent
            sx={{ display: "flex", alignItems: "center", flexWrap: "wrap" }}
          >
            <Icons
              iconName="ArrowBack"
              fontSize="large"
              style={{
                backgroundColor: theme.palette.primary.main,
                color: "#fff",
                marginRight: 20,
              }}
              to="/Unidades"
              tooltip={t("buttons.back")}
            />
            <Typography variant="h3" sx={{ flex: "1", fontWeight: "bold" }}>
              {t("units.importUnits.title")}
            </Typography>
          </CardContent>
          <Divider sx={{ maxWidth: "calc(100% - 32px)", margin: "auto" }} />
          <Card
            variant="outlined"
            sx={{
              overflow: "hidden",
              boxShadow: "0 0 10px rgba(0, 0, 0, 0.1)",
              border: "1px solid rgba(0, 0, 0, 0.1)",
              borderRadius: 2,
              maxWidth: "calc(100% - 32px)",
              margin: "auto",
              marginTop: 2,
            }}
          >
            <CardContent sx={{ maxWidth: "calc(100% - 64px)", margin: "auto" }}>
              <CardContent
                sx={{
                  display: "flex",
                  alignItems: "center",
                  margin: -2,
                  flexWrap: "wrap",
                }}
              >
                <Typography
                  variant="h5"
                  sx={{
                    flex: "1",
                    color: theme.palette.primary.main,
                  }}
                >
                  {t("units.importUnits.importFile")}
                </Typography>
                <Icons
                  iconName="AssignmentReturned"
                  fontSize="large"
                  style={{
                    marginLeft: "auto",
                    backgroundColor: theme.palette.primary.main,
                    color: theme.palette.white.main,
                  }}
                  onClick={handleDownload}
                  tooltip={t("buttons.downloadTemplate")}
                />
                <Icons
                  iconName="Close"
                  fontSize="large"
                  onClick={handleClearData}
                  style={{
                    marginLeft: "15px",
                    backgroundColor: theme.palette.danger.main,
                    color: theme.palette.white.main,
                  }}
                  tooltip={t("buttons.clear")}
                />
              </CardContent>
              <Divider sx={{ maxWidth: "100%", marginTop: 2 }} />
              <CardContent>
                <Grid container spacing={2}>
                  <Grid item xs={12}>
                    {error && (
                      <Alert severity="error" sx={{ marginBottom: 2 }}>
                        {error}
                      </Alert>
                    )}
                  </Grid>
                  <Grid item xs={12}>
                    <div
                      onDrop={handleFileDrop}
                      onDragOver={handleDragOver}
                      style={{
                        textAlign: "center",
                        padding: "3px",
                        border: "2px dashed #aaa",
                        borderRadius: "4px",
                        cursor: "pointer",
                        "&:hover": {
                          backgroundColor: theme.palette.primary.main,
                        },
                      }}
                    >
                      <input
                        type="file"
                        id="file_Documento"
                        multiple
                        onChange={handleFileUpload}
                        style={{ display: "none" }}
                        ref={fileInputRef}
                      />
                      {excelData.length !== 0 ? (
                        <Box>
                          <Icons
                            iconName="TextSnippet"
                            fontSize="large"
                            style={{
                              marginBottom: "1px",
                              color: theme.palette.primary.main,
                            }}
                          />
                          <Typography>
                            {fileName} - {fileSize} bytes
                          </Typography>
                        </Box>
                      ) : (
                        <label htmlFor="file_Documento">
                          <div>
                            <Icons
                              fontSize="60px"
                              iconName="CloudUpload"
                              style={{
                                marginBottom: "1px",
                                color: theme.palette.primary.main,
                              }}
                            />
                            <Typography>
                              {t("buttons.dragOrClick")}
                            </Typography>
                          </div>
                        </label>
                      )}
                    </div>
                  </Grid>
                </Grid>
              </CardContent>
            </CardContent>
          </Card>
          <Card
            variant="outlined"
            sx={{
              overflow: "hidden",
              boxShadow: "0 0 10px rgba(0, 0, 0, 0.1)",
              border: "1px solid rgba(0, 0, 0, 0.1)",
              borderRadius: 2,
              maxWidth: "calc(100% - 32px)",
              margin: "auto",
              marginTop: 2,
            }}
          >
            <CardContent sx={{ maxWidth: "calc(100% - 64px)", margin: "auto" }}>
              <Typography
                variant="h5"
                sx={{
                  flex: "1",
                  color: theme.palette.primary.main,
                }}
              >
                {t("units.importUnits.unitsList")}
              </Typography>
              <Divider sx={{ maxWidth: "100%", marginTop: 2 }} />
              <Paper
                sx={{
                  width: "100%",
                  margin: "auto",
                  overflow: "hidden",
                  boxShadow: "none",
                  border: "none",
                }}
              >
                <TableContainer>
                  <Table>
                    <TableHead>
                      <TableRow>
                        <TableCell style={{ fontWeight: "bold" }} key="vin">
                          {t("units.vin")}
                        </TableCell>
                        <TableCell style={{ fontWeight: "bold" }} key="modelo">
                          {t("units.addOrEdit.model")}
                        </TableCell>
                        <TableCell style={{ fontWeight: "bold" }} key="marca">
                          {t("units.brand")}
                        </TableCell>
                        <TableCell style={{ fontWeight: "bold" }} key="año">
                          {t("units.addOrEdit.year")}
                        </TableCell>
                        <TableCell style={{ fontWeight: "bold" }} key="cliente">
                          {t("units.client")}
                        </TableCell>
                        <TableCell
                          style={{ fontWeight: "bold" }}
                          key="ubicacion"
                        >
                          {t("units.addOrEdit.originLocation")}
                        </TableCell>
                        <TableCell
                          style={{ fontWeight: "bold" }}
                          key="ubicacionDestino"
                        >
                          {t("units.addOrEdit.destinationLocation")}
                        </TableCell>
                        <TableCell
                          style={{ fontWeight: "bold" }}
                          key="mensaje"
                          sx={{ width: "30%" }}
                        >
                          {t("message")}
                        </TableCell>
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      {!excelData || excelData.length === 0 ? (
                        <TableRow>
                          <TableCell colSpan={13 + 1} align="center">
                            {t("units.importUnits.noUnitsAvailable")}
                          </TableCell>
                        </TableRow>
                      ) : (
                        excelData.map((row, rowIndex) => (
                          <TableRow key={rowIndex}>
                            <TableCell>{row.vin}</TableCell>
                            <TableCell>{row.modelo}</TableCell>
                            <TableCell>{row.marca}</TableCell>
                            <TableCell>{row.anio}</TableCell>
                            <TableCell>{row.cliente}</TableCell>
                            <TableCell>{row.ubicacion}</TableCell>
                            <TableCell>{row.ubicacionDestino}</TableCell>
                            <TableCell sx={{ minWidth: "250px" }}>
                              {row.mensaje.length > 0 ? (
                                <List
                                  sx={{ maxHeight: "150px", overflowY: "auto" }}
                                >
                                  {row.mensaje.map((msg, index) => (
                                    <ListItem
                                      key={index}
                                      sx={{
                                        color:
                                          row.estatus === true
                                            ? theme.palette.primary.main
                                            : theme.palette.danger.main,
                                      }}
                                    >
                                      <ListItemText primary={msg} />
                                    </ListItem>
                                  ))}
                                </List>
                              ) : (
                                ""
                              )}
                            </TableCell>
                          </TableRow>
                        ))
                      )}
                    </TableBody>
                  </Table>
                </TableContainer>
              </Paper>
              <Grid container justifyContent="flex-end" sx={{ marginTop: 2 }}>
                <Button
                  variant="contained"
                  color="primary"
                  onClick={handleSubmit}
                >
                  {t("buttons.save")}
                </Button>
              </Grid>
            </CardContent>
          </Card>
        </CardContent>
      </Card>
    </>
  );
}
